import React, { useState, CSSProperties } from 'react';
import { Formik } from 'formik';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

import { View, TextInput, Logo, Button2, FormErrorMessage } from '../components';
import { Images, Colors, auth } from '../config2';
import firebaseDB from '../config2/firebase';
import { useTogglePasswordVisibility } from '../hooks';
import { loginValidationSchema } from '../utils';

import { en, es } from '../localizations';

const i18n = {
  t: (key: string) => {
    const translations = es as any;
    return translations[key] || key;
  }
};

export const LoginScreen = () => {
  const navigate = useNavigate();
  const [errorState, setErrorState] = useState('');
  const { passwordVisibility, handlePasswordVisibility, rightIcon } =
    useTogglePasswordVisibility();

  const handleLogin = async (values: { email: string; password: string }) => {
    const { email, password } = values;
    setErrorState('');
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user has admin role
      const userDocRef = doc(firebaseDB, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        await auth.signOut();
        setErrorState('User profile not found. Please contact an administrator.');
        return;
      }
      
      const userData = userDoc.data();
      
      if (userData.role !== 'admin') {
        await auth.signOut();
        setErrorState('Access denied. Administrator access only.');
        return;
      }
      
      // If user is admin, authentication will succeed
    } catch (error) {
      setErrorState(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const containerStyle: CSSProperties = {
    flex: 1,
    backgroundColor: Colors.white,
    paddingLeft: '12px',
    paddingRight: '12px',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  };

  const logoContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '40px',
  };

  const screenTitleStyle: CSSProperties = {
    fontSize: '28px',
    fontWeight: '700',
    color: Colors.black,
    paddingTop: '20px',
  };

  const footerStyle: CSSProperties = {
    backgroundColor: Colors.white,
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingBottom: '48px',
    display: 'flex',
    alignItems: 'center',
  };

  const buttonStyle: CSSProperties = {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '8px',
    backgroundColor: Colors.brown_border,
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
  };

  const buttonTextStyle: CSSProperties = {
    fontSize: '20px',
    color: Colors.white,
    fontWeight: '700',
  };

  const borderlessButtonContainerStyle: CSSProperties = {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <>
      <View style={containerStyle}>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <View style={logoContainerStyle}>
            <Logo uri={Images.logo} />
            <h1 style={screenTitleStyle}>{i18n.t('login')}</h1>
          </View>
          <Formik
            initialValues={{
              email: '',
              password: ''
            }}
            validationSchema={loginValidationSchema}
            onSubmit={values => handleLogin(values)}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleSubmit,
              handleBlur
            }) => (
              <>
                <TextInput
                  name='email'
                  leftIconName='email'
                  placeholder={i18n.t('mail')}
                  autoCapitalize='none'
                  type='email'
                  autoFocus={true}
                  value={values.email}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                />
                <FormErrorMessage
                  error={errors.email}
                  visible={touched.email}
                />
                <TextInput
                  name='password'
                  leftIconName='key-variant'
                  placeholder={i18n.t('password')}
                  autoCapitalize='none'
                  autoCorrect='off'
                  type={passwordVisibility ? 'password' : 'text'}
                  rightIcon={rightIcon}
                  handlePasswordVisibility={handlePasswordVisibility}
                  value={values.password}
                  onChange={handleChange('password')}
                  onBlur={handleBlur('password')}
                />
                <FormErrorMessage
                  error={errors.password}
                  visible={touched.password}
                />
                {errorState !== '' ? (
                  <FormErrorMessage error={errorState} visible={true} />
                ) : null}
                <button style={buttonStyle} onClick={() => handleSubmit()}>
                  <span style={buttonTextStyle}>Iniciar Sesion</span>
                </button>
              </>
            )}
          </Formik>
          <Button2
            style={borderlessButtonContainerStyle}
            borderless
            title={i18n.t('createNewAccount')}
            onPress={() => navigate('/signup')}
          />
          <Button2
            style={borderlessButtonContainerStyle}
            borderless
            title={i18n.t('resetPassword')}
            onPress={() => navigate('/forgot-password')}
          />
        </div>
      </View>

      <View style={footerStyle}>
        <p style={{ fontSize: '14px', fontWeight: '700', color: Colors.dessert_sand }}>
        </p>
      </View>
    </>
  );
};
